import Dexie from 'dexie';
import type { HealthRecord, BloodPressureRecord, BloodSugarRecord, MedicalExamIndicator } from '@/types';

class HealthDatabase extends Dexie {
  healthRecords!: Dexie.Table<HealthRecord, number>;
  bloodPressureRecords!: Dexie.Table<BloodPressureRecord, number>;
  bloodSugarRecords!: Dexie.Table<BloodSugarRecord, number>;
  medicalExamIndicators!: Dexie.Table<MedicalExamIndicator, number>;

  constructor() {
    super('HealthMonitorDB');
    this.version(1).stores({
      healthRecords: '++id, date, type, createdAt',
      bloodPressureRecords: '++id, recordId, date, systolic, diastolic',
      bloodSugarRecords: '++id, recordId, date, value, type',
      medicalExamIndicators: '++id, recordId, name, value'
    });
  }
}

const db = new HealthDatabase();

export const HealthDB = {
  // Health Records
  async addHealthRecord(record: Omit<HealthRecord, 'id' | 'createdAt'>): Promise<number> {
    const id = await db.healthRecords.add({
      ...record,
      createdAt: Date.now()
    });
    return id;
  },

  // 导入云端记录（支持指定 ID）
  async importHealthRecord(record: HealthRecord): Promise<void> {
    await db.healthRecords.put(record);
  },

  async getHealthRecords(): Promise<HealthRecord[]> {
    return db.healthRecords.orderBy('date').reverse().toArray();
  },

  async getHealthRecordsByDateRange(startDate: string, endDate: string): Promise<HealthRecord[]> {
    return db.healthRecords
      .where('date')
      .between(startDate, endDate)
      .reverse()
      .sortBy('date');
  },

  async deleteHealthRecord(id: number): Promise<void> {
    await db.healthRecords.delete(id);
    await db.bloodPressureRecords.where('recordId').equals(id).delete();
    await db.bloodSugarRecords.where('recordId').equals(id).delete();
    await db.medicalExamIndicators.where('recordId').equals(id).delete();
  },

  // Blood Pressure Records
  async addBloodPressureRecord(record: Omit<BloodPressureRecord, 'id'>): Promise<number> {
    return db.bloodPressureRecords.add(record as BloodPressureRecord);
  },

  // 导入云端血压记录（支持指定 ID）
  async importBloodPressureRecord(record: BloodPressureRecord): Promise<void> {
    await db.bloodPressureRecords.put(record);
  },

  async getBloodPressureRecords(recordId?: number): Promise<BloodPressureRecord[]> {
    if (recordId) {
      return db.bloodPressureRecords.where('recordId').equals(recordId).toArray();
    }
    return db.bloodPressureRecords.orderBy('date').reverse().toArray();
  },

  async getBloodPressureRecordsByDateRange(startDate: string, endDate: string): Promise<BloodPressureRecord[]> {
    return db.bloodPressureRecords
      .where('date')
      .between(startDate, endDate)
      .reverse()
      .sortBy('date');
  },

  // Blood Sugar Records
  async addBloodSugarRecord(record: Omit<BloodSugarRecord, 'id'>): Promise<number> {
    return db.bloodSugarRecords.add(record as BloodSugarRecord);
  },

  // 导入云端血糖记录（支持指定 ID）
  async importBloodSugarRecord(record: BloodSugarRecord): Promise<void> {
    await db.bloodSugarRecords.put(record);
  },

  async getBloodSugarRecords(recordId?: number): Promise<BloodSugarRecord[]> {
    if (recordId) {
      return db.bloodSugarRecords.where('recordId').equals(recordId).toArray();
    }
    return db.bloodSugarRecords.orderBy('date').reverse().toArray();
  },

  async getBloodSugarRecordsByDateRange(startDate: string, endDate: string): Promise<BloodSugarRecord[]> {
    return db.bloodSugarRecords
      .where('date')
      .between(startDate, endDate)
      .reverse()
      .sortBy('date');
  },

  // Medical Exam Indicators
  async addMedicalExamIndicator(record: Omit<MedicalExamIndicator, 'id'>): Promise<number> {
    return db.medicalExamIndicators.add(record as MedicalExamIndicator);
  },

  async getMedicalExamIndicators(recordId?: number): Promise<MedicalExamIndicator[]> {
    if (recordId) {
      return db.medicalExamIndicators.where('recordId').equals(recordId).toArray();
    }
    return db.medicalExamIndicators.toArray();
  },

  // Combined Queries
  async getRecentRecords(days: number): Promise<{
    bp: BloodPressureRecord[];
    bs: BloodSugarRecord[];
  }> {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const [bp, bs] = await Promise.all([
      this.getBloodPressureRecordsByDateRange(startDate, endDate),
      this.getBloodSugarRecordsByDateRange(startDate, endDate)
    ]);
    
    return { bp, bs };
  },

  // Statistics
  async getStats(): Promise<{
    totalRecords: number;
    bpRecords: number;
    bsRecords: number;
  }> {
    const [totalRecords, bpRecords, bsRecords] = await Promise.all([
      db.healthRecords.count(),
      db.bloodPressureRecords.count(),
      db.bloodSugarRecords.count()
    ]);
    
    return { totalRecords, bpRecords, bsRecords };
  },

  // Export all data
  async exportAllData(): Promise<{
    healthRecords: HealthRecord[];
    bloodPressureRecords: BloodPressureRecord[];
    bloodSugarRecords: BloodSugarRecord[];
    medicalExamIndicators: MedicalExamIndicator[];
  }> {
    const [healthRecords, bloodPressureRecords, bloodSugarRecords, medicalExamIndicators] = await Promise.all([
      db.healthRecords.toArray(),
      db.bloodPressureRecords.toArray(),
      db.bloodSugarRecords.toArray(),
      db.medicalExamIndicators.toArray()
    ]);
    
    return { healthRecords, bloodPressureRecords, bloodSugarRecords, medicalExamIndicators };
  },

  // Clear all data
  async clearAllData(): Promise<void> {
    await Promise.all([
      db.healthRecords.clear(),
      db.bloodPressureRecords.clear(),
      db.bloodSugarRecords.clear(),
      db.medicalExamIndicators.clear()
    ]);
  }
};

export default db;
